import clsx from 'clsx';
import React from 'react';
import ReactHtmlParser, { convertNodeToElement } from 'react-html-parser';

const YOUTUBE_EMBED_PATTERN = /youtube\.com\/embed\//;

type HtmlParserNode = {
  attribs?: Record<string, string>;
  children?: HtmlParserNode[];
  data?: string;
  name?: string;
  type: string;
};

const isHtmlParserTagNode = (node: HtmlParserNode) =>
  node.type === 'tag' && typeof node.name === 'string';

const appendClassName = (existing: string | undefined, className: string) =>
  existing ? `${existing} ${className}` : className;

const isYouTubeEmbedIframeNode = (node: HtmlParserNode) =>
  isHtmlParserTagNode(node) &&
  node.name === 'iframe' &&
  YOUTUBE_EMBED_PATTERN.test(node.attribs.src ?? '');

const featuredGraphicTransform = (
  node: HtmlParserNode,
  index: number
): React.ReactElement | string | null => {
  if (isYouTubeEmbedIframeNode(node)) {
    return convertNodeToElement(
      {
        ...node,
        attribs: {
          ...node.attribs,
          class: appendClassName(
            node.attribs?.class,
            'absolute left-0 top-0 h-full w-full border-0'
          ),
        },
      },
      index,
      featuredGraphicTransform
    );
  }

  if (
    isHtmlParserTagNode(node) &&
    ['div', 'p'].includes(node.name) &&
    node.children?.some(isYouTubeEmbedIframeNode)
  ) {
    return convertNodeToElement(
      {
        ...node,
        attribs: {
          ...node.attribs,
          class: appendClassName(node.attribs?.class, 'relative m-0 h-full w-full'),
        },
      },
      index,
      featuredGraphicTransform
    );
  }

  return convertNodeToElement(node, index, featuredGraphicTransform);
};

const parseFeaturedGraphic = (featuredGraphic: string) =>
  ReactHtmlParser(featuredGraphic, { transform: featuredGraphicTransform });

const isYouTubeEmbedFeaturedGraphic = (featuredGraphic: string): boolean =>
  YOUTUBE_EMBED_PATTERN.test(featuredGraphic);

interface FeaturedGraphicProps {
  'aria-label'?: string;
  className?: string;
  featuredGraphic: string;
  isSEOBotAndArticleIsNotFree?: boolean;
}

const EmbeddedMedia = ({
  'aria-label': ariaLabel,
  className,
  featuredGraphic,
  isSEOBotAndArticleIsNotFree,
}: FeaturedGraphicProps): JSX.Element => {
  const isYouTubeEmbed = isYouTubeEmbedFeaturedGraphic(featuredGraphic);

  return (
    <div
      aria-label={ariaLabel}
      className={clsx(className, {
        'seo-paywall-content': isSEOBotAndArticleIsNotFree,
        'relative mb-20 aspect-[16/9] w-full': isYouTubeEmbed,
      })}
      data-nosnippet
    >
      {parseFeaturedGraphic(featuredGraphic)}
    </div>
  );
};

export default EmbeddedMedia;
